import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { TokenIcon } from '@web3icons/react/dynamic';

// Custom components
import Card from 'components/card/Card';
import * as React from 'react';
import { RowObj } from '../variables/tableDataComplex';
import { formatPrice } from 'utils/number';

const columnHelper = createColumnHelper<RowObj>();

export default function InvestmentPortfolio(props: { tableData: any }) {
  const { tableData } = props;
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  let defaultData = tableData;
  const columns = [
    columnHelper.accessor('coin', {
      id: 'coin',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          COIN
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center" gap={{ sm: '6px', lg: '10px' }}>
          <TokenIcon symbol={info.getValue()} size={30} variant="branded" />
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('quantity', {
      id: 'quantity',
      header: () => (
        <Flex align="center" justify="center" w="100%">
          <Text
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            QUANTITY
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Flex align="center" justify="center">
          <Text color={textColor} fontSize="sm">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('averageCost', {
      id: 'averageCost',
      header: () => (
        <Flex align="center" justify="center" w="100%">
          <Text
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            AVG COST
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Flex align="center" justify="center">
          <Text color={textColor} fontSize="sm">
            {formatPrice(info.getValue())}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('currentPrice', {
      id: 'currentPrice',
      header: () => (
        <Flex align="center" justify="center" w="100%">
          <Text
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            CURRENT PRICE
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Flex align="center" justify="center">
          <Text color={textColor} fontSize="sm">
            {formatPrice(info.getValue())}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('marketValue', {
      id: 'marketValue',
      header: () => (
        <Flex align="center" justify="center" w="100%">
          <Text
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            MARKET VALUE
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Flex align="center" justify="center">
          <Text color={textColor} fontSize="sm">
            {formatPrice(info.getValue())}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('unrealizedPnl', {
      id: 'unrealizedPnl',
      header: () => (
        <Flex align="center" justify="center" w="100%">
          <Text
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            UNREALIZED PNL
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Flex align="center" justify="center">
          <Text
            color={info.getValue() >= 0 ? 'green.400' : 'red.400'}
            fontSize="sm"
            fontWeight="700"
          >
            {`${info.getValue() > 0 ? '+' : ''}${formatPrice(info.getValue())}`}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('pnlPercent', {
      id: 'pnlPercent',
      header: () => (
        <Flex align="center" justify="center" w="100%">
          <Text
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            PnL(%)
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Flex align="center" justify="center">
          <Text
            color={info.getValue() >= 0 ? 'green.400' : 'red.400'}
            fontSize="sm"
            fontWeight="700"
          >
            {`${info.getValue() > 0 ? '+' : ''}${info.getValue()}`}%
          </Text>
        </Flex>
      ),
    }),
  ];

  const [data, setData] = React.useState(() => [...defaultData]);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Portfolio
        </Text>
      </Flex>
      <Box>
        <Table variant="simple" color="gray.500" mb="24px" mt="12px">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Flex
                        justifyContent="space-between"
                        align="center"
                        fontSize={{ sm: '10px', lg: '12px' }}
                        color="gray.400"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {{
                          asc: '',
                          desc: '',
                        }[header.column.getIsSorted() as string] ?? null}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table
              .getRowModel()
              .rows.slice(0, 5)
              .map((row) => {
                return (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <Td
                          key={cell.id}
                          fontSize={{ sm: '14px' }}
                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                          borderColor="transparent"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
}
